import React, { useState } from "react";
import { X, Crown, Check, Sparkle, Lightning, ArrowSquareOut, PaypalLogo } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { shouldHidePaywall } from "../lib/platform";
import { STRIPE_PAYMENT_LINKS } from "../lib/stripeLinks";

// Three subscription tiers exactly matching the user's price card:
//   • $12  — 3-Month Intro    (package_id: quarter)
//   • $39  — Annual Supporter  (package_id: annual)     ← Most Popular
//   • $99  — Lifetime          (package_id: lifetime)
// These IDs map to STRIPE_PACKAGES in backend/server.py.
const PLANS = [
  {
    id: "quarter",
    price: "$12",
    cadence: "for 3 months",
    label: "Intro",
    tagline: "Try premium without commitment",
    perks: ["Unlock every movie, sermon, song", "Cast to TV", "HD studio voices"],
    cta: "Start 3 Months · $12",
    accent: "bg-cream-200 text-forest",
  },
  {
    id: "annual",
    price: "$39",
    cadence: "for the year",
    label: "Most Popular",
    tagline: "Best value — save 73% vs monthly",
    perks: [
      "Everything in Intro",
      "Foundational Black-American Films",
      "AI Devotionals & Chat — unlimited",
      "Offline downloads",
    ],
    cta: "Start Annual · $39",
    accent: "bg-terracotta text-cream",
    featured: true,
  },
  {
    id: "lifetime",
    price: "$99",
    cadence: "one time",
    label: "Lifetime",
    tagline: "Pay once, never again",
    perks: [
      "Everything forever",
      "Founder badge on your profile",
      "Early access to new features",
    ],
    cta: "Get Lifetime · $99",
    accent: "bg-forest text-cream",
  },
];

const REASON_HEADLINES = {
  premium: "Unlock the full library",
  movie: "This movie is premium",
  sermon: "This sermon is premium",
  comedy: "This clip is premium",
  song: "This track is premium",
  apocrypha: "Apocrypha + Dead Sea Scrolls are premium",
  cast: "Cast to TV is premium",
  download: "Offline downloads are premium",
  ai: "Unlimited AI is premium",
  voice: "HD studio voices are premium",
};

const REASON_BLURBS = {
  premium: "Bible + daily verse + history stay free forever. Upgrade to unlock everything else.",
  movie: "First 7 movies in every section are free. Upgrade to unlock the rest.",
  sermon: "First 7 sermons are free. Upgrade for full access to the sermon vault.",
  comedy: "First 7 clips are free. Upgrade to unlock the full comedy lineup.",
  song: "First 7 songs in every category are free. Upgrade for the full library.",
  apocrypha: "The hidden books — Jasher, Dead Sea Scrolls, Apocrypha — are reserved for premium.",
  cast: "Send Holy Words content to your TV via Chromecast / AirPlay.",
  download: "Save sermons + songs to your device for offline listening.",
  ai: "Unlimited AI devotionals + chat. Free tier is 3 / day.",
  voice: "Nova, Shimmer, Onyx, Alloy and every HD studio voice.",
};

export default function PaywallModal({ open, onClose, reason = "premium", itemTitle = "" }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(null); // package_id being processed

  if (!open) return null;

  const headline = REASON_HEADLINES[reason] || REASON_HEADLINES.premium;
  const blurb = REASON_BLURBS[reason] || REASON_BLURBS.premium;

  const startCheckout = async (packageId, method = "card") => {
    if (!user || user === false) {
      // Anonymous — punt to login with a return path. Stripe checkout
      // is best-on-record (so receipts have an email), but anon checkout
      // also works — the user will just be prompted for email at Stripe.
      toast("Sign in first so we can keep your subscription on your account.");
      try { window.location.assign("/login?next=/support"); } catch (_) {}
      return;
    }
    setBusy(method === "paypal" ? `${packageId}-pp` : packageId);
    try {
      const endpoint = method === "paypal" ? "/paypal/order" : "/checkout/session";
      const { data } = await api.post(endpoint, {
        package_id: packageId,
        origin_url: window.location.origin,
        email: user?.email,
      });
      if (data?.url) {
        // Native shell: open in a new tab like the Stripe payment links
        // (some WebView wrappers block same-window external navigation).
        if (shouldHidePaywall()) {
          const w = window.open(data.url, "_blank", "noopener");
          if (!w) window.location.assign(data.url);
        } else {
          window.location.assign(data.url);
        }
        return;
      }
      toast.error("Couldn't start checkout — try again");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Checkout unavailable right now.";
      toast.error(typeof msg === "string" ? msg : "Checkout error");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-ink/85 backdrop-blur-md grid place-items-center p-3 sm:p-6 animate-fade-in"
      data-testid="paywall-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl max-h-[95dvh] overflow-y-auto bg-cream rounded-3xl shadow-2xl"
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="paywall-close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-ink/10 hover:bg-ink/20 grid place-items-center text-ink"
        >
          <X size={18} weight="bold" />
        </button>

        {/* Header */}
        <div className="px-6 sm:px-10 pt-10 pb-6 text-center bg-gradient-to-b from-cream to-cream-100 rounded-t-3xl">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-terracotta/15 text-terracotta mb-3">
            <Crown size={26} weight="fill" />
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl text-forest leading-tight" data-testid="paywall-headline">
            {headline}
          </h2>
          {itemTitle && (
            <p className="text-muted_ink text-sm mt-2 italic">{itemTitle}</p>
          )}
          <p className="text-ink/75 text-sm sm:text-base mt-3 max-w-xl mx-auto">{blurb}</p>
        </div>

        {/* Plans — replaced with hosted buy.stripe.com payment links
            when running inside the Android / iOS native shell. Direct
            Stripe links bypass the PWA intent-filter that would
            otherwise re-route customers back into the locked app. */}
        {shouldHidePaywall() ? (
          <div className="px-4 sm:px-8 pb-6 grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
            {[
              { id: "quarter",  title: "3-Month Intro", price: "$12",  note: "Try Premium — save 20%", url: STRIPE_PAYMENT_LINKS.quarter  },
              { id: "annual",   title: "Annual",        price: "$39",  note: "Most Popular · save 35%", url: STRIPE_PAYMENT_LINKS.annual,  popular: true },
              { id: "lifetime", title: "Lifetime",      price: "$99",  note: "Best Value · forever",   url: STRIPE_PAYMENT_LINKS.lifetime },
            ].map((tier) => (
              <div key={tier.id} className="flex flex-col gap-2">
                <a
                  href={tier.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`paywall-native-${tier.id}`}
                  className={`flex-1 rounded-2xl border-2 p-4 sm:p-5 flex flex-col gap-2 transition-all ${
                    tier.popular ? "border-terracotta bg-cream shadow-md" : "border-forest bg-white hover:bg-cream"
                  }`}
                >
                  {tier.popular && (
                    <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-terracotta">
                      Most Popular
                    </span>
                  )}
                  <span className="font-heading text-lg text-forest font-medium">{tier.title}</span>
                  <span className="text-2xl font-bold text-ink leading-none">{tier.price}</span>
                  <span className="text-xs text-muted_ink">{tier.note}</span>
                  <span className="mt-2 inline-flex items-center justify-center gap-1 px-4 h-9 rounded-full bg-forest text-cream text-[11px] tracking-[0.18em] uppercase font-bold">
                    <Crown size={12} weight="fill" /> Pay with card
                  </span>
                </a>
                <button
                  type="button"
                  onClick={() => startCheckout(tier.id, "paypal")}
                  disabled={busy !== null}
                  data-testid={`paywall-native-paypal-${tier.id}`}
                  className="h-9 rounded-full text-xs font-bold inline-flex items-center justify-center gap-1.5 bg-[#003087] text-white hover:bg-[#00256b] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PaypalLogo size={13} weight="bold" /> {busy === `${tier.id}-pp` ? "Opening PayPal…" : "Pay with PayPal"}
                </button>
              </div>
            ))}
          </div>
        ) : (
        <div className="px-4 sm:px-8 pb-6 grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          {PLANS.map((p) => (
            <div
              key={p.id}
              data-testid={`paywall-plan-${p.id}`}
              className={`relative rounded-2xl p-5 flex flex-col ${
                p.featured
                  ? "bg-forest text-cream ring-2 ring-terracotta shadow-xl"
                  : "bg-cream-100 text-ink border border-cream-300"
              }`}
            >
              {p.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-terracotta hover:text-terracotta/80 text-[10px] uppercase tracking-[0.18em] font-bold flex items-center gap-1 shadow-md">
                  <Sparkle size={10} weight="fill" /> Most Popular
                </div>
              )}
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-70 mb-1">{p.label}</div>
              <div className="flex items-baseline gap-1">
                <div className={`text-4xl font-heading ${p.featured ? "text-cream" : "text-forest"}`}>{p.price}</div>
                <div className="text-xs opacity-70">{p.cadence}</div>
              </div>
              <div className="text-sm opacity-80 mt-2 mb-4 leading-snug">{p.tagline}</div>
              <ul className="space-y-1.5 text-sm flex-1">
                {p.perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-1.5">
                    <Check size={14} weight="bold" className={`mt-0.5 shrink-0 ${p.featured ? "text-terracotta" : "text-forest"}`} />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => startCheckout(p.id)}
                disabled={busy !== null}
                data-testid={`paywall-cta-${p.id}`}
                className={`mt-4 h-12 rounded-full font-bold text-sm uppercase tracking-wider transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${p.accent}`}
              >
                {busy === p.id ? "Opening checkout…" : p.cta}
              </button>
              <button
                type="button"
                onClick={() => startCheckout(p.id, "paypal")}
                disabled={busy !== null}
                data-testid={`paywall-paypal-${p.id}`}
                className={`mt-2 h-9 rounded-full text-xs font-bold inline-flex items-center justify-center gap-1.5 border transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${
                  p.featured ? "border-cream/30 text-cream hover:bg-cream/10" : "border-forest/25 text-forest hover:bg-forest/5"
                }`}
              >
                <PaypalLogo size={13} weight="bold" /> {busy === `${p.id}-pp` ? "Opening PayPal…" : "Pay with PayPal"}
              </button>
            </div>
          ))}
        </div>
        )}

        {/* Trust badges — web only (Stripe mention hidden inside the
            native shell where Stripe is not the active checkout). */}
        {!shouldHidePaywall() && (
        <div className="px-6 pb-8 text-center text-[11px] uppercase tracking-[0.2em] text-muted_ink font-semibold flex items-center justify-center gap-4 flex-wrap">
          <span className="inline-flex items-center gap-1"><Lightning size={11} weight="fill" /> Cancel anytime</span>
          <span>·</span>
          <span>Secure checkout · Stripe &amp; PayPal</span>
          <span>·</span>
          <span>No hidden fees</span>
        </div>
        )}
      </div>
    </div>
  );
}
